import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Calendar, Clock, ArrowLeft, MapPin, Share2, Phone, MessageCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { getPostBySlug, getPostsByRegion, BlogPost as BlogPostType } from "@/data/blogPosts";

const regionColors: Record<string, string> = {
  Monaco: "bg-gold/20 text-gold border-gold/30",
  Dubai: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  Italy: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  Spain: "bg-rose-500/20 text-rose-400 border-rose-500/30",
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = getPostsByRegion(post.region)
    .filter((p) => p.id !== post.id)
    .slice(0, 3);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleCall = () => {
    window.location.href = "tel:+37068628485";
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/37068628485?text=Hi, I just read your article "${post.title}" and I'm interested in ${post.region} properties.`, "_blank");
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Agota Estate</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.tags.join(", ")} />
        <link rel="canonical" href={`https://agotaestate.com/blog/${post.slug}`} />
        
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:type" content="article" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.image,
            "datePublished": post.publishedAt,
            "author": {
              "@type": "Person",
              "name": post.author,
            },
            "publisher": {
              "@type": "Organization",
              "name": "Agota Estate",
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://agotaestate.com/blog/${post.slug}`,
            },
          })}
        </script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="relative pt-24 pb-12">
          <div className="absolute inset-0 h-[60vh]">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-charcoal/40 to-background" />
          </div>

          <div className="relative container mx-auto px-6 lg:px-12 pt-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-cream/70 hover:text-gold transition-colors mb-6"
              >
                <ArrowLeft size={16} />
                <span className="font-body text-sm uppercase tracking-wider">Back to Blog</span>
              </Link>

              <div className="flex items-center gap-4 mb-6">
                <span className={`px-4 py-2 text-xs uppercase tracking-wider border ${regionColors[post.region]}`}>
                  <MapPin size={12} className="inline mr-1" />
                  {post.region}
                </span>
                <span className="font-body text-xs uppercase tracking-wider text-cream/60">
                  {post.category}
                </span>
              </div>

              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-cream mb-6 leading-tight">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 text-cream/60">
                <span className="font-body text-sm">By {post.author}</span>
                <span className="flex items-center gap-1 text-sm">
                  <Calendar size={14} />
                  {new Date(post.publishedAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
                <span className="flex items-center gap-1 text-sm">
                  <Clock size={14} />
                  {post.readTime} min read
                </span>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-1 text-sm hover:text-gold transition-colors"
                >
                  <Share2 size={14} />
                  Share
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:col-span-2"
              >
                <div className="prose prose-lg max-w-none prose-headings:font-display prose-headings:font-light prose-p:font-body prose-p:text-muted-foreground prose-strong:text-foreground prose-li:text-muted-foreground">
                  <p className="text-xl text-foreground font-body leading-relaxed mb-8">
                    {post.excerpt}
                  </p>
                  <div 
                    className="blog-content"
                    dangerouslySetInnerHTML={{ 
                      __html: post.content
                        .split('\n')
                        .map(line => {
                          if (line.startsWith('## ')) {
                            return `<h2 class="text-2xl md:text-3xl mt-12 mb-6">${line.replace('## ', '')}</h2>`;
                          }
                          if (line.startsWith('### ')) {
                            return `<h3 class="text-xl md:text-2xl mt-8 mb-4">${line.replace('### ', '')}</h3>`;
                          }
                          if (line.startsWith('**') && line.endsWith('**')) {
                            return `<p class="font-semibold text-foreground mt-6 mb-2">${line.replace(/\*\*/g, '')}</p>`;
                          }
                          if (line.startsWith('- ')) {
                            return `<li class="ml-4">${line.replace('- ', '')}</li>`;
                          }
                          if (line.trim() === '') {
                            return '';
                          }
                          return `<p class="mb-4">${line}</p>`;
                        })
                        .join('')
                    }}
                  />
                </div>

                {/* Tags */}
                <div className="mt-12 pt-8 border-t border-border">
                  <span className="font-body text-sm text-muted-foreground mr-4">Tags:</span>
                  <div className="inline-flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-muted text-muted-foreground text-xs uppercase tracking-wider"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>

              {/* Sidebar */}
              <aside className="lg:col-span-1">
                <div className="sticky top-32 space-y-8">
                  {/* CTA Card */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="bg-charcoal p-6 text-cream"
                  >
                    <h3 className="font-display text-xl mb-3">
                      Interested in {post.region}?
                    </h3>
                    <p className="font-body text-sm text-cream/70 mb-6">
                      Our team specializes in exceptional properties across {post.region}. 
                      Let's discuss your requirements.
                    </p>
                    <div className="space-y-3">
                      <Button 
                        variant="luxury" 
                        className="w-full"
                        onClick={handleCall}
                      >
                        <Phone size={16} className="mr-2" />
                        Call Now
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full border-cream/30 text-cream hover:bg-cream/10"
                        onClick={handleWhatsApp}
                      >
                        <MessageCircle size={16} className="mr-2" />
                        WhatsApp
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="w-full text-cream/70 hover:text-cream hover:bg-cream/10"
                        asChild
                      >
                        <Link to={`/properties?region=${post.region}`}>
                          View {post.region} Properties
                        </Link>
                      </Button>
                    </div>
                  </motion.div>

                  {/* Related Posts */}
                  {relatedPosts.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                    >
                      <h3 className="font-display text-lg mb-4">More from {post.region}</h3>
                      <div className="space-y-4">
                        {relatedPosts.map((relatedPost) => (
                          <Link
                            key={relatedPost.id}
                            to={`/blog/${relatedPost.slug}`}
                            className="block group"
                          >
                            <div className="flex gap-4">
                              <img
                                src={relatedPost.image}
                                alt={relatedPost.title}
                                className="w-20 h-20 object-cover flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-body text-sm text-foreground group-hover:text-gold transition-colors line-clamp-2">
                                  {relatedPost.title}
                                </h4>
                                <span className="text-xs text-muted-foreground">
                                  {relatedPost.readTime} min read
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-2xl md:text-3xl text-foreground mb-4">
                Ready to Explore {post.region}?
              </h2>
              <p className="font-body text-muted-foreground mb-8 max-w-xl mx-auto">
                Access our exclusive portfolio of {post.region} properties, 
                many available only through private introduction.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="luxury" size="lg" asChild>
                  <Link to="/properties">View All Properties</Link>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <a href="/#contact">Schedule Consultation</a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default BlogPost;
